import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Skeleton,
  Select,
  MenuItem,
  Tooltip,
  Button,
  TextField,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { AppDispatch, RootState } from '../../store/store';
import { getInsights } from '../../rtk-apis/dashboard/getInsights';
import { type BillingBasis } from '../../rtk-apis/dashboard/updateClinicBilling';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { monthlyStudyCounts } from '../../Utils/pacsStudyCounts';
import { getInvoices, type InvoiceRecord } from '../../rtk-apis/dashboard/billing';

/* ════════════════════════════════════════════════════════════════════════
   Super Admin dashboard — cross-clinic performance and revenue.

   Built for BILLING, so the guiding rule is that a number is either exact or
   visibly marked as not exact. Two honesty mechanisms run through this screen:

   1. Studies are bucketed by DICOM AE title, and some AE titles serve more than
      one clinic (Springwood and Yarrabilba share one). Those clinics show an
      "AE shared" flag; their study figure cannot be split. The organisation
      total counts each shared bucket once, so it will be LESS than the sum of
      the rows — that is correct, not a bug.
   2. A clinic with no rate set is listed as unpriced rather than counted as
      zero revenue, so it cannot silently vanish from the total.
   ════════════════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const chartBase: Highcharts.Options = {
  credits: { enabled: false },
  title: { text: undefined },
  legend: { itemStyle: { color: T.ink2, fontWeight: '500' }, itemHoverStyle: { color: T.ink } },
  chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
  xAxis: {
    labels: { style: { color: T.ink3 } },
    lineColor: T.line,
    tickColor: T.line,
  },
  yAxis: {
    title: { text: undefined },
    labels: { style: { color: T.ink3 } },
    gridLineColor: T.line,
  },
  tooltip: {
    backgroundColor: T.surface2,
    borderColor: T.lineStrong,
    style: { color: T.ink },
  },
};

const money = (n: number, currency = 'AUD') => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString()}`;
  }
};

export const cardSx = {
  background: `linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))`,
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  p: '18px 20px',
  minWidth: 0,
};

interface ClinicRow {
  clinicId: string;
  name: string;
  reports: number;
  echo: number;
  stress: number;
  reportedStudies: number;
  studies: number;
  studiesApprox: boolean;
  aeTitles: string[];
  billing: {
    basis: BillingBasis;
    rate: number;
    currency: string;
    paymentTermsDays?: number;
    autoInvoice?: {
      enabled?: boolean;
      frequency?: 'weekly' | 'fortnightly' | 'monthly';
      anchorDay?: number;
      autoSend?: boolean;
      nextRunOn?: string;
    };
  };
  billableCount: number;
  revenue: number;
  prevReports: number;
  reportsDelta: number | null;
  avgTatHours: number | null;
  statusMix: Record<string, number>;
  unreported: number;
}

interface InsightsTotals {
  avgRevenuePerClinic?: number;
  newClinics?: number;
  avgTatHours?: number | null;
  unreported?: number;
  clinics?: number;
  activeClinics?: number;
  reports?: number;
  echo?: number;
  stress?: number;
  studies?: number;
  reportedStudies?: number;
  revenue?: number;
  revenueApprox?: boolean;
  currency?: string;
  unconfiguredCount?: number;
}

interface SuperAdminInsights {
  period: { year: number; month: number | 'all'; label: string };
  totals: InsightsTotals;
  unconfigured: { clinicId: string; name: string }[];
  clinics: ClinicRow[];
  trend: { categories: string[]; series: { name: string; data: number[] }[] };
  statusTotals: Record<string, number>;
  weekdayVolume: { name: string; count: number }[];
  topReporters: { name: string; clinic: string; reports: number; finalized: number }[];
  distribution: { total: number; delivered: number; failed: number; successRate: number | null };
}

export const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
}> = ({ icon, label, value, sub, warn }) => (
  <Box sx={cardSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: warn ? T.amber : T.teal }}>
      {icon}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: T.ink3,
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#fff', mt: '6px', lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: 12, color: warn ? T.amber : T.ink3, mt: '2px' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const SuperAdminDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  // getInsights writes to getInsightsResponse, NOT the legacy getDashboardDataResponse
  // slice the old super-admin view used.
  const { loading } = useSelector((s: RootState) => s.getInsightsResponse);

  // Read once on mount. Calling new Date() in the render body would produce a fresh
  // object every render and invalidate any memo that depends on it.
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | 'all'>(() => new Date().getMonth() + 1);
  const [data, setData] = useState<SuperAdminInsights | null>(null);
  // Explicit date range. Both ends must be set before it overrides the month/year —
  // the server ignores a half-supplied range for the same reason.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const rangeActive = !!(from && to && from <= to);
  // The invoice register drives the cash-position card. The full register lives on
  // its own Money page; this fetch exists only to summarise cash on the dashboard.
  const [billing, setBilling] = useState<{ invoices: InvoiceRecord[] }>({ invoices: [] });
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 2021 + 1 }, (_, i) => 2021 + i).reverse();
  }, []);

  /**
   * Studies received, counted by the PACS itself.
   *
   * Held separately from `data` because it comes from a different system: the archive
   * knows every study it received, while the backend figure only counts studies that also
   * survived the conversion pipeline and is known to under-report. Null means the archive
   * did not answer — the chart then falls back to the backend series and says so, rather
   * than drawing zeros nobody can justify.
   */
  const [pacsStudies, setPacsStudies] = React.useState<(number | null)[] | null>(null);
  const [pacsChecked, setPacsChecked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setPacsChecked(false);
    monthlyStudyCounts(year)
      .then(series => {
        if (!cancelled) {
          setPacsStudies(series);
        }
      })
      .catch(err => {
        console.warn('[dashboard] PACS study counts unavailable:', err);
        if (!cancelled) {
          setPacsStudies(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPacsChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [year]);

  const load = React.useCallback(async () => {
    try {
      const res = (await dispatch(
        getInsights(rangeActive ? { year, month, from, to } : { year, month })
      ).unwrap()) as {
        status?: string;
        data?: SuperAdminInsights;
      };
      if (res?.status === 'SUCCESS' && res.data) {
        setData(res.data);
      }
    } catch (err) {
      // Logged, not swallowed. A silent catch here once turned a thrown error into a
      // page full of legitimate-looking zeros, which took far longer to diagnose than
      // a stack trace would have. The previous figures stay on screen either way.
      console.error('[dashboard] insights fetch failed:', err);
    }
  }, [dispatch, year, month, from, to, rangeActive]);

  const loadBilling = React.useCallback(async () => {
    try {
      const res = (await dispatch(getInvoices({ year })).unwrap()) as {
        data?: { invoices: InvoiceRecord[] };
      };
      setBilling({ invoices: res?.data?.invoices || [] });
    } catch (err) {
      console.error('[dashboard] invoice fetch failed:', err);
    }
  }, [dispatch, year]);

  useEffect(() => {
    load();
    loadBilling();
  }, [load, loadBilling]);

  // Memoised so the `||` fallback doesn't hand a fresh array to the revenue chart's
  // memo on every render.
  const rows: ClinicRow[] = useMemo(() => data?.clinics || [], [data]);
  const totals: InsightsTotals = data?.totals || {};
  const currency = totals.currency || 'AUD';

  /**
   * The studies line: the archive's own count where it answered, the backend's pipeline
   * figure for any month it did not. Mixing them per-month is deliberate — a single
   * unanswered month should cost that month's accuracy, not the whole series.
   */
  const studiesFromPacs = !!pacsStudies;
  const studiesSeries = React.useMemo(() => {
    const backend = data?.trend?.series?.[2]?.data || [];
    // Copy, never hand over the store's own array. Highcharts writes into the data array
    // in place (setData -> updateData), and Redux state is frozen in development, so
    // passing the reference throws "Cannot assign to read only property '0'".
    return pacsStudies
      ? pacsStudies.map((n, i) => (typeof n === 'number' ? n : (backend[i] ?? 0)))
      : [...backend];
  }, [pacsStudies, data]);

  const trendOptions: Highcharts.Options = useMemo(
    () => ({
      ...chartBase,
      // Deliberately 2D columns + a line. The previous super-admin charts were
      // rendered with Highcharts 3D, which distorts bar heights by perspective —
      // unusable for figures that get billed on.
      chart: { ...chartBase.chart, type: 'column' },
      xAxis: { ...chartBase.xAxis, categories: data?.trend?.categories || MONTHS },
      series: [
        {
          type: 'column',
          name: 'Echo',
          data: [...(data?.trend?.series?.[0]?.data || [])],
          color: T.sky,
        },
        {
          type: 'column',
          name: 'Stress Echo',
          data: [...(data?.trend?.series?.[1]?.data || [])],
          color: T.violet,
        },
        {
          type: 'line',
          name: 'Studies received',
          data: studiesSeries,
          color: T.teal,
          marker: { enabled: false },
        },
      ] as Highcharts.SeriesOptionsType[],
    }),
    [data, studiesSeries]
  );

  const statusOptions: Highcharts.Options = useMemo(() => {
    const st = data?.statusTotals || {};
    const palette: Record<string, string> = {
      Draft: T.amber,
      Preliminary: T.sky,
      Finalized: T.green,
    };
    const entries = Object.entries(st).filter(([, v]) => (v as number) > 0);
    return {
      ...chartBase,
      // A flat donut, not a 3D pie: perspective makes the near slice look bigger than
      // it is, which is exactly the distortion being removed from this dashboard.
      chart: { ...chartBase.chart, type: 'pie', height: 250 },
      plotOptions: {
        pie: {
          innerSize: '62%',
          borderWidth: 0,
          dataLabels: { enabled: false },
          showInLegend: true,
        },
      },
      series: [
        {
          type: 'pie',
          name: 'Reports',
          data: entries.map(([k, v]) => ({ name: k, y: v as number, color: palette[k] || T.ink3 })),
        },
      ] as Highcharts.SeriesOptionsType[],
    };
  }, [data]);

  const weekdayOptions: Highcharts.Options = useMemo(
    () => ({
      ...chartBase,
      chart: { ...chartBase.chart, type: 'column', height: 250 },
      xAxis: { ...chartBase.xAxis, categories: (data?.weekdayVolume || []).map(d => d.name) },
      legend: { enabled: false },
      series: [
        {
          type: 'column',
          name: 'Reports',
          data: (data?.weekdayVolume || []).map(d => d.count),
          color: T.teal,
        },
      ] as Highcharts.SeriesOptionsType[],
    }),
    [data]
  );

  /**
   * Cash position, derived from the invoice register rather than from projected
   * revenue. "Invoiced" is what was actually billed, which is not the same as the
   * revenue figure above: revenue counts activity in the selected period, invoiced
   * counts documents raised. Conflating them is how a business thinks it is owed
   * money it never billed for.
   */
  const cash = useMemo(() => {
    // Drafts are excluded as well as voided: an invoice that has not been issued is
    // not money billed, and counting it would show cash "outstanding" that no clinic
    // has ever been asked for. Same rule the server applies to receivables.
    const live = billing.invoices.filter(i => !i.voided && i.status !== 'DRAFT');
    const invoiced = live.reduce((a, i) => a + (i.total || 0), 0);
    const collected = live.reduce((a, i) => a + (i.amountPaid || 0), 0);
    const overdue = live.filter(i => i.daysOverdue > 0).reduce((a, i) => a + (i.balance || 0), 0);
    const awaiting = Math.max(0, invoiced - collected - overdue);

    const byClinic = new Map<string, { name: string; balance: number; overdue: boolean }>();
    live
      .filter(i => (i.balance || 0) > 0)
      .forEach(i => {
        const cur = byClinic.get(i.clinicId) || { name: i.clinicName, balance: 0, overdue: false };
        cur.balance += i.balance || 0;
        cur.overdue = cur.overdue || i.daysOverdue > 0;
        byClinic.set(i.clinicId, cur);
      });

    // How long clinics actually take to pay, measured from the invoice date to the
    // payment date. Only settled invoices count — an unpaid one has no answer yet, and
    // including it would flatter the figure.
    const settled = live.filter(i => i.status === 'PAID' && i.payments?.length);
    const avgDaysToPay = settled.length
      ? Math.round(
          settled.reduce((a, i) => {
            const last = i.payments[i.payments.length - 1];
            return (
              a +
              Math.max(
                0,
                (new Date(last.paidOn).getTime() - new Date(i.issuedOn).getTime()) / 86400000
              )
            );
          }, 0) / settled.length
        )
      : null;

    return {
      invoiced,
      collected,
      overdue,
      awaiting,
      count: live.length,
      paidCount: settled.length,
      avgDaysToPay,
      collectedPct: invoiced ? Math.round((collected / invoiced) * 100) : 0,
      debtors: [...byClinic.values()].sort((a, b) => b.balance - a.balance).slice(0, 3),
    };
  }, [billing.invoices]);

  const sumOfRowStudies = rows.reduce((a, r) => a + (r.studies || 0), 0);
  const sharedAetInPlay = rows.some(r => r.studiesApprox);

  if (loading && !data) {
    return (
      <Box sx={{ p: '24px 30px' }}>
        <Skeleton
          variant="rectangular"
          height={120}
          sx={{ borderRadius: '16px', mb: 2 }}
        />
        <Skeleton
          variant="rectangular"
          height={320}
          sx={{ borderRadius: '16px' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: '20px 30px 40px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {/* header + period picker */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: '18px',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
            Business Overview
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.ink3, mt: '3px' }}>
            Volume and revenue across every clinic · {data?.period?.label || ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Select
            size="small"
            value={month}
            onChange={e => setMonth(e.target.value as number | 'all')}
            disabled={rangeActive}
            sx={{
              minWidth: 130,
              color: T.ink,
              '.MuiOutlinedInput-notchedOutline': { borderColor: T.line },
            }}
          >
            <MenuItem value="all">Whole year</MenuItem>
            {MONTHS.map((m, i) => (
              <MenuItem
                key={m}
                value={i + 1}
              >
                {m}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            disabled={rangeActive}
            sx={{
              minWidth: 100,
              color: T.ink,
              '.MuiOutlinedInput-notchedOutline': { borderColor: T.line },
            }}
          >
            {years.map(y => (
              <MenuItem
                key={y}
                value={y}
              >
                {y}
              </MenuItem>
            ))}
          </Select>

          {/* Explicit date range. It supersedes month/year once BOTH ends are set, so
              the month/year controls grey out to show which period is actually in
              force rather than leaving two contradictory pickers both looking live. */}
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={e => setFrom(e.target.value)}
            sx={{ width: 158 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={e => setTo(e.target.value)}
            sx={{ width: 158 }}
          />
          {(from || to) && (
            <Button
              size="small"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              sx={{ color: T.ink3, textTransform: 'none' }}
            >
              Clear range
            </Button>
          )}
        </Box>
      </Box>

      {/* KPIs */}
      <Box
        sx={{
          display: 'grid',
          // Fixed track counts, NOT auto-fit: with auto-fit the 8 cards packed 7 across
          // and left a single orphan on row two. Explicit 2/4 columns always divide 8
          // evenly at every breakpoint.
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: '14px',
          mb: '16px',
        }}
      >
        <Kpi
          icon={<PaymentsOutlinedIcon fontSize="small" />}
          label="Revenue"
          value={money(totals.revenue || 0, currency)}
          sub={
            totals.unconfiguredCount
              ? `${totals.unconfiguredCount} clinic${totals.unconfiguredCount === 1 ? '' : 's'} unpriced`
              : totals.revenueApprox
                ? 'Approximate — shared AE title'
                : 'All clinics priced'
          }
          warn={!!totals.unconfiguredCount || !!totals.revenueApprox}
        />
        <Kpi
          icon={<DescriptionOutlinedIcon fontSize="small" />}
          label="Reports"
          value={String(totals.reports ?? 0)}
          sub={`${totals.echo ?? 0} echo · ${totals.stress ?? 0} stress`}
        />
        <Kpi
          icon={<MonitorHeartOutlinedIcon fontSize="small" />}
          label="Studies received"
          value={String(totals.studies ?? 0)}
          sub={`${totals.reportedStudies ?? 0} produced a report`}
        />
        <Kpi
          icon={<LocalHospitalOutlinedIcon fontSize="small" />}
          label="Active clinics"
          value={`${totals.activeClinics ?? 0} / ${totals.clinics ?? 0}`}
          sub={
            totals.newClinics ? `${totals.newClinics} new this period` : 'With activity this period'
          }
        />
        <Kpi
          icon={<TimerOutlinedIcon fontSize="small" />}
          label="Avg turnaround"
          value={totals.avgTatHours != null ? `${totals.avgTatHours} h` : '—'}
          sub="Creation to finalise, all clinics"
        />
        <Kpi
          icon={<PendingActionsOutlinedIcon fontSize="small" />}
          label="Unreported studies"
          value={String(totals.unreported ?? 0)}
          sub="Received but never reported"
          warn={(totals.unreported ?? 0) > 0}
        />
        <Kpi
          icon={<SendOutlinedIcon fontSize="small" />}
          label="Distribution"
          value={
            data?.distribution?.successRate != null ? `${data.distribution.successRate}%` : '—'
          }
          sub={`${data?.distribution?.delivered ?? 0} delivered · ${data?.distribution?.failed ?? 0} failed`}
          warn={!!data?.distribution?.failed}
        />
        <Kpi
          icon={<PaymentsOutlinedIcon fontSize="small" />}
          label="Avg per clinic"
          value={money(totals.avgRevenuePerClinic || 0, currency)}
          sub="Across priced clinics only"
        />
      </Box>

      {/* the reconciliation Nik asked for: studies vs reports */}
      {(totals.studies ?? 0) > 0 && (
        <Box
          sx={{
            ...cardSx,
            mb: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberOutlinedIcon
            fontSize="small"
            sx={{ color: T.ink3 }}
          />
          <Typography sx={{ fontSize: 13, color: T.ink2 }}>
            <b style={{ color: T.ink }}>{totals.studies}</b> studies received ·{' '}
            <b style={{ color: T.ink }}>{totals.reports}</b> reports created ·{' '}
            <b
              style={{
                color:
                  (totals.studies ?? 0) - (totals.reportedStudies ?? 0) > 0 ? T.amber : T.green,
              }}
            >
              {Math.max(0, (totals.studies ?? 0) - (totals.reportedStudies ?? 0))}
            </b>{' '}
            studies with no report yet.
            {sharedAetInPlay && (
              <>
                {' '}
                Rows below sum to {sumOfRowStudies} studies because some clinics share an AE title;
                the organisation total counts each shared feed once.
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* charts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' },
          // Cards size to their own content instead of stretching to match the tallest
          // in the row. Without this the cash card was padded out to the height of the
          // volume chart beside it, leaving a large empty panel.
          alignItems: 'start',
          gap: '14px',
          mb: '16px',
        }}
      >
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '10px' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
              Monthly volume · {year}
            </Typography>
            {/* The pipeline figure is known to under-report, so a fallback is labelled
                rather than passed off as the archive's count. */}
            {pacsChecked && !studiesFromPacs && (
              <Tooltip title="The imaging archive could not be reached, so study counts come from the conversion pipeline and may be lower than the number actually received.">
                <Box
                  sx={{
                    px: 0.9,
                    py: 0.2,
                    borderRadius: 5,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: T.bg,
                    bgcolor: T.amber,
                    cursor: 'default',
                  }}
                >
                  STUDIES ESTIMATED
                </Box>
              </Tooltip>
            )}
          </Box>
          <HighchartsReact
            highcharts={Highcharts}
            options={trendOptions}
          />
        </Box>
        {/* Cash position.
            Two previous attempts here (a bar chart, then a ranked leaderboard) both
            restated revenue per clinic — which the table further down already shows in
            full, with more detail. Repeating it added nothing. This panel answers a
            question nothing else on the page answers: of what we billed, how much has
            actually arrived, and who is holding it up. That only became possible once
            invoices and payments were persisted. */}
        <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '4px' }}>
            Cash position
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: T.ink3, mb: '16px' }}>
            Against everything invoiced to date
          </Typography>

          {cash.invoiced > 0 ? (
            <>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.05 }}>
                {money(cash.collected, currency)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: T.ink3, mb: '14px' }}>
                collected of {money(cash.invoiced, currency)} invoiced ·{' '}
                <b style={{ color: cash.collectedPct >= 80 ? T.green : T.amber }}>
                  {cash.collectedPct}%
                </b>
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  height: 10,
                  borderRadius: 99,
                  overflow: 'hidden',
                  mb: '12px',
                }}
              >
                {[
                  { v: cash.collected, c: T.green, label: 'Collected' },
                  { v: cash.awaiting, c: T.sky, label: 'Not yet due' },
                  { v: cash.overdue, c: T.red, label: 'Overdue' },
                ]
                  .filter(x => x.v > 0)
                  .map(x => (
                    <Tooltip
                      key={x.label}
                      title={`${x.label}: ${money(x.v, currency)}`}
                    >
                      <Box sx={{ width: `${(x.v / cash.invoiced) * 100}%`, background: x.c }} />
                    </Tooltip>
                  ))}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px', mb: '16px' }}>
                {[
                  { label: 'Collected', v: cash.collected, c: T.green },
                  { label: 'Not yet due', v: cash.awaiting, c: T.sky },
                  { label: 'Overdue', v: cash.overdue, c: T.red },
                ].map(x => (
                  <Box
                    key={x.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: x.c }} />
                    <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>
                      {x.label} <b style={{ color: T.ink }}>{money(x.v, currency)}</b>
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Counts, not just amounts: "3 of 4 settled" describes how the billing
                  relationship is actually going in a way a dollar figure alone cannot,
                  and gives the card substance when everything happens to be paid. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px',
                  borderTop: `1px solid ${T.line}`,
                  pt: '12px',
                  mb: cash.debtors.length ? '12px' : 0,
                }}
              >
                {[
                  { k: 'Invoices', v: String(cash.count) },
                  { k: 'Settled', v: `${cash.paidCount} of ${cash.count}` },
                  {
                    k: 'Avg days to pay',
                    v: cash.avgDaysToPay != null ? String(cash.avgDaysToPay) : '—',
                  },
                ].map(x => (
                  <Box key={x.k}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: T.ink3,
                      }}
                    >
                      {x.k}
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, mt: '2px' }}>
                      {x.v}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {cash.debtors.length > 0 && (
                <Box sx={{ borderTop: `1px solid ${T.line}`, pt: '12px' }}>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: T.ink3,
                      mb: '8px',
                    }}
                  >
                    Owing the most
                  </Typography>
                  {cash.debtors.map(d => (
                    <Box
                      key={d.name}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: '10px',
                        py: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: T.ink2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {d.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: d.overdue ? T.red : T.ink,
                          flexShrink: 0,
                        }}
                      >
                        {money(d.balance, currency)}
                        {d.overdue ? ' · overdue' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: T.ink3 }}>
              Nothing invoiced yet. Raise an invoice from the clinic table below and this will show
              what has been billed, what has been collected and what is overdue.
            </Typography>
          )}
        </Box>
      </Box>

      {/* report status mix · weekday load · top reporters */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1.2fr' },
          gap: '14px',
          mb: '16px',
        }}
      >
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '10px' }}>
            Report status mix
          </Typography>
          <HighchartsReact
            highcharts={Highcharts}
            options={statusOptions}
          />
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '10px' }}>
            Reports by weekday
          </Typography>
          <HighchartsReact
            highcharts={Highcharts}
            options={weekdayOptions}
          />
        </Box>
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: '10px' }}>
            Busiest reporters
          </Typography>
          {(data?.topReporters || []).length ? (
            (data?.topReporters || []).slice(0, 7).map(d => (
              <Box
                key={`${d.name}-${d.clinic}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  py: '7px',
                  borderBottom: `1px solid ${T.line}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: T.ink,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {d.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: T.ink3 }}>{d.clinic}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                    {d.reports}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: T.ink3 }}>
                    {d.finalized} finalised
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: 13, color: T.ink3 }}>
              No assigned reports in this period.
            </Typography>
          )}
        </Box>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.sev || 'success'}
          onClose={() => setToast(null)}
          variant="filled"
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminDashboard;
